import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import {Typography, Stack, Grid, useTheme, TextField} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LoadingButton} from '@mui/lab';

import CollapsibleTable from '../../../../sub-modules-menu/collapsible-table/CollapsibleTable';
import DataGridComponent from '@components/datagrid/DataGridComponent';
import {deepCopy, shadowBoxSx} from '../../util';
import {worklistDataCopy, agingDataCopy} from '../data';
import ClaimLevelView from '../claim-level-view/ClaimLevelView';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const facilityLocationsDropdownValues = [
  'Albany Medical Center',
  'Columbia Memorial Hospital',
  'Glen Falls Hospital',
  'Saratoga Hospital'
];

const posDropdownValues = [
  'Inpatient',
  'Outpatient',
  'ASC',
  'Emergency Room',
  'Lab'
];

const modalityDropdownValues = [
  'Radiology',
  'Surgery',
  'Physical Therapy',
  'Oncology'
];

const financialClassDropdownValues = [
  'Medicare',
  'Medicaid',
  'Medicare MCO',
  'Medicare HMO',
  'Medicare Supp Plan',
  'Tricare/Champus/Champva',
  'Commercial',
  "Worker's Comp",
  'Auto PIP',
  'Patient'
];

const responsibilityDropdownValues = [
  'Practice',
  'Interscripts',
  'ABC Collection Agency'
];

const cobOrderDropdownValues = ['Primary', 'Secondary', 'Tertiary'];

const ARWorklist = (props: any) => {
  const [facilityLocation, setFacilityLocation] = React.useState<string[]>([]);
  const [placeOfService, setPlaceOfService] = React.useState<string[]>([]);
  const [modality, setModality] = React.useState<string[]>([]);
  const [financialClass, setFinancialClass] = React.useState<string[]>([]);
  const [cobOrder, setCobOrder] = React.useState<string[]>([]);
  const [responsibility, setResponsibility] = React.useState<string[]>([]);
  const [startDate, setStartDate] = React.useState<any>(null);
  const [endDate, setEndDate] = React.useState<any>(null);
  const [worklistData, setWorklistData] = React.useState<any>(null);
  const [agingData, setAgingData] = React.useState<any>([]);
  const [displayClaimLevelView, setDisplayClaimLevelView] =
    React.useState(false);
  const [selectedFacilityLocation, setSelectedFacilityLocation] =
    React.useState('');
  const [selectedMRNInfo, setSelectedMRNInfo] = React.useState<any>(null);

  const theme = useTheme();

  const loadData = () => {
    setWorklistData(deepCopy(worklistDataCopy));
    setAgingData(deepCopy(agingDataCopy));
  };

  const searchDisabledStatus = () => {
    let disabledStatus = true;
    const filterValues1 = [
      facilityLocation,
      placeOfService,
      modality,
      financialClass,
      cobOrder,
      responsibility
    ];
    for (let i = 0; i < filterValues1.length; i++) {
      if (filterValues1[i].length) {
        disabledStatus = false;
        break;
      }
    }
    if (disabledStatus) {
      disabledStatus = startDate || endDate ? false : true;
    }
    return disabledStatus;
  };

  const onResetFilters = () => {
    setFacilityLocation([]);
    setPlaceOfService([]);
    setModality([]);
    setFinancialClass([]);
    setCobOrder([]);
    setResponsibility([]);
    setStartDate(null);
    setEndDate(null);
  };

  const onCellClick = (params: any) => {
    if (params && params.field === 'facilityLocation') {
      setDisplayClaimLevelView(true);
      setSelectedMRNInfo({...params.row});
    }
  };

  const onNavigateToMain = () => {
    setDisplayClaimLevelView(false);
    setSelectedMRNInfo(null);
  };

  const getMultiSelectValues = (type: string) => {
    let dropdownVals: string | any[] = [];
    switch (type) {
      case 'facility_location':
        dropdownVals = facilityLocationsDropdownValues;
        break;
      case 'pos':
        dropdownVals = posDropdownValues;
        break;
      case 'modality':
        dropdownVals = modalityDropdownValues;
        break;
      case 'financial_class':
        dropdownVals = financialClassDropdownValues;
        break;
      case 'cob_order':
        dropdownVals = cobOrderDropdownValues;
        break;
      case 'responsibility':
        dropdownVals = responsibilityDropdownValues;
        break;
      default:
        break;
    }
    return dropdownVals.length ? dropdownVals : [];
  };

  const getMultiSelectValue = (type: string) => {
    switch (type) {
      case 'facility_location':
        return facilityLocation;
      case 'pos':
        return placeOfService;
      case 'modality':
        return modality;
      case 'financial_class':
        return financialClass;
      case 'cob_order':
        return cobOrder;
      case 'responsibility':
        return responsibility;
      default:
        return [];
    }
  };

  const onChangeMultiSelect = (e: any, type: string) => {
    let selectedValue =
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;
    if (selectedValue[selectedValue.length - 1] === 'all') {
      const dropdownValues = getMultiSelectValues(type);
      selectedValue =
        selectedValue.length - 1 === dropdownValues.length
          ? []
          : dropdownValues;
    }
    switch (type) {
      case 'facility_location':
        setFacilityLocation(selectedValue);
        break;
      case 'pos':
        setPlaceOfService(selectedValue);
        break;
      case 'modality':
        setModality(selectedValue);
        break;
      case 'financial_class':
        setFinancialClass(selectedValue);
        break;
      case 'cob_order':
        setCobOrder(selectedValue);
        break;
      case 'responsibility':
        setResponsibility(selectedValue);
        break;
      default:
        break;
    }
  };

  const getMultiSelectLabel = (type: string) => {
    switch (type) {
      case 'facility_location':
        return 'Facility/Location';
      case 'pos':
        return 'POS';
      case 'modality':
        return 'Modality';
      case 'financial_class':
        return 'Financial Class';
      case 'cob_order':
        return 'COB Order';
      case 'responsibility':
        return 'Responsibility';
      default:
        return '';
    }
  };

  const renderMultiSelect = (type: string) => {
    const selectedValue = getMultiSelectValue(type) || [];
    const dropdownValues = getMultiSelectValues(type) || [];
    const isAllSelected =
      dropdownValues.length > 0 &&
      selectedValue.length === dropdownValues.length;
    return (
      <FormControl>
        <InputLabel id={`${type}_label`}>
          {getMultiSelectLabel(type)}
        </InputLabel>
        <Select
          id={`${type}_type`}
          labelId={`${type}_label`}
          multiple
          fullWidth
          value={selectedValue}
          label={getMultiSelectLabel(type)}
          onChange={(e: any) => onChangeMultiSelect(e, type)}
          renderValue={(selected: any) => selected.join(', ')}
          MenuProps={MenuProps}
          sx={{
            borderRadius: '8px',
            border: '0px',
            color: 'rgb(33, 43, 54)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0000003b',
              borderWidth: '1.8px'
            }
          }}>
          <MenuItem value="all">
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selectedValue.length > 0 &&
                selectedValue.length < dropdownValues.length
              }
            />
            <Typography>Select All</Typography>
          </MenuItem>
          {dropdownValues.map(val => (
            <MenuItem key={val} value={val}>
              <Checkbox checked={selectedValue.includes(val)} />
              <Typography>{val}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const agingBucketGridColumns = [
    {
      field: 'agingBucket',
      headerName: 'Aging Bucket',
      flex: 1,
      width: 175,
      renderCell: (params: any) => {
        if (params.value === 'Weightage (%)') {
          return (
            <Typography sx={{fontWeight: 'bold'}}>{params.value}</Typography>
          );
        }
        return params.value;
      }
    },
    {
      field: 'days1',
      headerName: '0-30 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '16%') {
          return (
            <Typography color="success.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days2',
      headerName: '31-60 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '17%') {
          return (
            <Typography color="success.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days3',
      headerName: '61-90 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '7%') {
          return (
            <Typography color="success.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days4',
      headerName: '91-120 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '2%') {
          return (
            <Typography color="success.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days5',
      headerName: '121-180 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '17%') {
          return (
            <Typography color="error.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days6',
      headerName: '181-365 Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '12%') {
          return (
            <Typography color="error.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    },
    {
      field: 'days7',
      headerName: '365+ Days',
      flex: 1,
      renderCell: (params: any) => {
        if (params.value === '28%') {
          return (
            <Typography color="error.main" sx={{fontWeight: 'bold'}}>
              {params.value}
            </Typography>
          );
        }
      }
    }
  ];

  const worklistGridColumns = [
    {
      label: 'Attachments',
      data_column: 'attachments'
    },
    {
      label: 'Facility/Location',
      data_column: 'facilityLocation',
      is_clickable: true,
      navigation_needed: true
    },
    {
      label: 'Type Of Bill',
      data_column: 'typeOfBill'
    },
    {
      label: 'Place Of Service',
      data_column: 'placeOfService'
    },
    {
      label: '837 Claim ID',
      data_column: 'claimId837'
    },
    {
      label: 'MRN',
      data_column: 'MRN'
    },
    {
      label: 'Date Of Service',
      data_column: 'dateOfService'
    },
    {
      label: 'Revenue Code',
      data_column: 'revenueCode'
    },
    {
      label: 'CPT/HCPCS',
      data_column: 'cptHcpcs'
    },
    {
      label: 'Current Financial Class',
      data_column: 'currentFinancialClass'
    },
    {
      label: 'Insurance',
      data_column: 'insurance'
    },
    {
      label: 'COB Order',
      data_column: 'cobOrder'
    },
    {
      label: 'Billed Amount',
      data_column: 'billedAmount',
      aggregation_currency_formatter_required: true
    },
    {
      label: 'Outstanding',
      data_column: 'outstandingAmount',
      aggregation_currency_formatter_required: true
    },
    {
      label: 'Denial Category',
      data_column: 'denialCategory'
    },
    {
      label: 'Follow Up Date',
      data_column: 'followUpDate'
    },
    {
      label: 'Assigned To',
      data_column: 'assignedTo'
    },
    {
      label: 'Assigned On',
      data_column: 'assignedOn'
    },
    {
      label: 'Comments',
      data_column: 'comments'
    }
  ];

  return (
    <Box px={3} mt={1}>
      {displayClaimLevelView ? (
        <ClaimLevelView
          facilityLocation={selectedFacilityLocation}
          mrnData={selectedMRNInfo}
          onNavigateToMain={onNavigateToMain}
        />
      ) : (
        <>
          <Box sx={{...shadowBoxSx}}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(4, 1fr)'
              }}>
              {renderMultiSelect('facility_location')}
              {renderMultiSelect('pos')}
              {renderMultiSelect('modality')}
              {renderMultiSelect('financial_class')}
              {renderMultiSelect('cob_order')}
              {renderMultiSelect('responsibility')}
            </Box>

            <Typography
              sx={{
                fontSize: '14px !important',
                fontWeight: 700,
                marginTop: '32px'
              }}>
              Date Billed
            </Typography>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              sx={{marginTop: '16px'}}
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(4, 1fr)'
              }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={setStartDate}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0000003b',
                      borderWidth: '1.8px'
                    }
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={setEndDate}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0000003b',
                      borderWidth: '1.8px'
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Stack spacing={3} alignItems="flex-end" sx={{mt: 2}}>
              <Grid item xs={12} display="flex" sm={6} md={12}>
                <LoadingButton
                  type="reset"
                  disabled={searchDisabledStatus()}
                  variant="outlined"
                  onClick={onResetFilters}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.light
                  }}>
                  Reset
                </LoadingButton>
                <LoadingButton
                  disabled={searchDisabledStatus()}
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.primary.dark
                  }}>
                  Search
                </LoadingButton>
              </Grid>
            </Stack>
          </Box>
          <Box sx={{...shadowBoxSx}}>
            <Typography sx={{fontWeight: 'bold', color: 'error.main'}}>
              <span style={{color: '#000'}}>*</span> 365+ Days AR is 20% higher
              than the industry standard.
            </Typography>
            <Typography sx={{fontWeight: 'bold', color: 'success.main'}}>
              <span style={{color: '#000'}}>*</span> All the aging buckets up to
              0-180 Days look great.
            </Typography>
            <DataGridComponent
              sectionUIData={[]}
              module={props?.module}
              sub_module={props?.submodule}
              columns={agingBucketGridColumns}
              rowHeight={37}
              rows={agingData}
              rowThreshold={0}
              initialState={{
                pinnedColumns: {
                  left: ['agingBucket']
                }
              }}
              getRowId={(row: any) => row.agingBucket}
              hideFooter={false}
              paginationMode="server"
              sortingMode="server"
              sortingOrder={['desc', 'asc']}
              autoHeight={false}
              checkboxSelection={false}
              rowsCount={0}
              sx={{paddingLeft: 0, paddingRight: 0}}
            />
            <CollapsibleTable
              sx={{
                paddingLeft: '0px',
                paddingRight: '0px'
              }}
              disableToolbar
              displayHorizontalScroll
              loading={false}
              sectionUIData={{fields: worklistGridColumns}}
              sectionRowsData={worklistData}
              module={props?.module}
              sub_module={props?.sub_module || 'ar-worklist'}
              minHeight="100px"
              onCellClick={onCellClick}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ARWorklist;
